import type { ReactNode } from 'react';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  message?: string;
  action?: ReactNode;
  variant?: 'default' | 'queue' | 'search' | 'error';
}

const illustrations: Record<string, ReactNode> = {
  default: (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="var(--accent-softer)" />
      <circle cx="60" cy="60" r="30" fill="var(--accent-soft)" />
      <path d="M48 55c0-2.2 1.8-4 4-4h16c2.2 0 4 1.8 4 4v14c0 2.2-1.8 4-4 4H52c-2.2 0-4-1.8-4-4V55z" fill="var(--accent)" opacity="0.3" />
      <path d="M55 62h10M60 57v10" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  queue: (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="var(--accent-softer)" />
      <rect x="35" y="40" width="50" height="40" rx="6" fill="var(--accent-soft)" />
      <circle cx="50" cy="55" r="6" fill="var(--accent)" opacity="0.4" />
      <circle cx="70" cy="55" r="6" fill="var(--accent)" opacity="0.3" />
      <path d="M45 70h30" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M60 32v-6M76 38l4-4M44 38l-4-4" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    </svg>
  ),
  search: (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="var(--accent-softer)" />
      <circle cx="55" cy="55" r="18" stroke="var(--accent)" strokeWidth="3" opacity="0.4" />
      <path d="M68 68l12 12" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
      <path d="M50 50l10 10M55 48v14" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" opacity="0.2" />
    </svg>
  ),
  error: (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="60" r="50" fill="var(--danger-soft)" />
      <circle cx="60" cy="60" r="24" stroke="var(--danger)" strokeWidth="3" opacity="0.4" />
      <path d="M52 52l16 16M68 52L52 68" stroke="var(--danger)" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
    </svg>
  ),
};

export function EmptyState({ icon, title, message, action, variant = 'default' }: EmptyStateProps) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '64px 24px', textAlign: 'center', gap: '16px',
    }}>
      {icon || illustrations[variant]}
      <h3 style={{
        fontFamily: 'var(--font-serif)', fontSize: '18px', color: 'var(--ink)',
        margin: 0, fontWeight: 400,
      }}>
        {title}
      </h3>
      {message && (
        <p style={{ fontSize: '14px', color: 'var(--ink-3)', margin: 0, maxWidth: '360px', lineHeight: 1.5 }}>
          {message}
        </p>
      )}
      {action}
    </div>
  );
}
